/**
 * Company-admin cancellation link: token check (Phase 3) + confirm execute (Phase 4).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CANCELLATION_CONFIRM_UI } from '../constants/accountCancellationWorkflow';
import {
  fetchCancellationConfirmTokenState,
  postCancellationConfirmComplete,
  type CancellationTokenPublicState,
} from '../services/cancellationConfirmApi';

export default function ConfirmCancellation(): React.ReactElement {
  const [searchParams] = useSearchParams();
  const token = searchParams.get(CANCELLATION_CONFIRM_UI.QUERY_PARAM) || '';
  const [tokenState, setTokenState] = useState<CancellationTokenPublicState | 'loading'>('loading');
  const [confirming, setConfirming] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (!token.trim()) {
      setTokenState('invalid');
      return;
    }
    let cancelled = false;
    void (async () => {
      const s = await fetchCancellationConfirmTokenState(token);
      if (!cancelled) {
        setTokenState(s);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const onConfirm = useCallback(async () => {
    if (!token.trim()) return;
    setConfirming(true);
    setActionError(null);
    const result = await postCancellationConfirmComplete(token);
    setConfirming(false);
    if (result.ok) {
      setTokenState('completed');
      return;
    }
    setActionError(result.message);
  }, [token]);

  const body = useMemo(() => {
    switch (tokenState) {
      case 'loading':
        return 'Checking your link…';
      case 'pending':
        return 'This link is valid. Confirm below to proceed with account cancellation for the user named in your email. Billing will be stopped, sign-in disabled, and access revoked per Tranzit policy.';
      case 'completed':
        return 'This cancellation has been completed. The user’s subscription has been cancelled, their login disabled, and access flags applied. Retained data is kept per retention policy.';
      case 'expired':
        return 'This link has expired. Contact Tranzit support if you still need help.';
      case 'not_available':
        return 'This request is no longer awaiting confirmation. Contact Tranzit support if you have questions.';
      case 'invalid':
      default:
        return 'This link is invalid or incomplete. Use the link from your email or contact support.';
    }
  }, [tokenState]);

  const showConfirm =
    tokenState === 'pending' && !confirming && token.trim().length > 0;

  return (
    <div
      style={{
        minHeight: '40vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        fontFamily: 'system-ui, sans-serif',
        maxWidth: 520,
        margin: '0 auto',
        textAlign: 'center',
      }}
    >
      <h1 style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>Account cancellation</h1>
      <p style={{ lineHeight: 1.5, color: '#333' }}>{body}</p>
      {showConfirm ? (
        <button
          type="button"
          onClick={() => void onConfirm()}
          style={{
            marginTop: '1.5rem',
            padding: '0.6rem 1.25rem',
            fontSize: '1rem',
            cursor: 'pointer',
            borderRadius: 6,
            border: '1px solid #333',
            background: '#1a1a1a',
            color: '#fff',
          }}
        >
          Confirm cancellation
        </button>
      ) : null}
      {confirming ? (
        <p style={{ marginTop: '1rem', color: '#555' }}>Processing…</p>
      ) : null}
      {actionError ? (
        <p style={{ marginTop: '1rem', color: '#b00020' }} role="alert">
          {actionError}
        </p>
      ) : null}
    </div>
  );
}
